<script lang="ts" setup>
import { computed } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ContentsWithLinks from "@/components/article/politiken/ContentsWithLinks.vue";
import { type PolitikenThesaurusArticle } from "@/types/article-formats/politiken";

const props = defineProps<{
  article: PolitikenThesaurusArticle;
  isCollapsible: boolean;
}>();

const hasSynonyms = computed<boolean>(() => props.article.synonyms.length > 0);
const hasHyponyms = computed<boolean>(() => props.article.hyponyms.length > 0);
const hasAntonyms = computed<boolean>(() => props.article.antonyms.length > 0);
const hasSublemmas = computed<boolean>(() => props.article.sublemmas.length > 0);

const isLast = (index: number, array: object[]) => index === array.length - 1;
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <ArticleContent>
    <div class="article-content__header article-header">
      <div class="article-header__content">
        <h5 class="article-header__word">{{ article.word.lemma }}</h5>
        <sup v-if="article.word.hnr" class="pol-header-sup">{{ article.word.hnr }}</sup>

        <template v-if="article.word.lemmaTwo">
          <span class="article-header__element article-header__element--chained">el.</span>
          <h5 class="article-header__word article-header__element article-header__element--chained">
            {{ article.word.lemmaTwo }}
          </h5>
        </template>
        <template v-if="article.word.alternativeSpelling">
          <span class="article-header__element article-header__element--chained">el.</span>
          <h5 class="article-header__word article-header__element article-header__element--chained">
            {{ article.word.alternativeSpelling }}
          </h5>
        </template>

        <span
          v-if="props.article.word.wordClass"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          {{ props.article.word.wordClass }}
        </span>
        <span
          v-if="props.article.word.info"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          ({{ props.article.word.info }})
        </span>
        <span
          v-if="article.word.usage"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          ({{ article.word.usage }})
        </span>
      </div>
    </div>

    <ArticleContentCollapser :is-collapsible="props.isCollapsible">
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div class="indentation-container">
          <span v-for="(synonym, index) in article.synonyms" :key="index">
            <span
              v-if="index > 0 && synonym.firstBnr"
              class="article-content__text"
              v-html="synonym.firstBnr"
            ></span>
            <b v-if="synonym.comment" class="article-content__text pr-2">{{ synonym.comment }}</b>
            <span class="article-content__text article-content__text--primary">{{
              synonym.synonym
            }}</span
            ><span v-if="synonym.rComment" class="article-content__text">
              (<span v-html="synonym.rComment"></span>)</span
            >
          </span>

          <div v-if="hasHyponyms" class="article-content__politiken-grid">
            <div class="article-content__text article-content__politiken-grid-label">Specielt:</div>
            <div class="article-content__politiken-grid-content">
              <template v-for="(hyponym, index) in article.hyponyms" :key="index">
                <span class="article-content__text article-content__text--primary">
                  {{ hyponym.hyponym }}</span
                ><span v-if="hyponym.rComment" class="article-content__text">
                  ({{ hyponym.rComment }})</span
                ><span v-if="!isLast(index, article.hyponyms)" class="article-content__text"
                  >,
                </span>
              </template>
            </div>
          </div>

          <div v-if="hasAntonyms" class="article-content__politiken-grid">
            <div class="article-content__text article-content__politiken-grid-label">Antonym:</div>
            <div class="article-content__politiken-grid-content">
              <span v-for="(antonym, index) in article.antonyms" :key="index">
                <span
                  class="article-content__text article-content__text--primary"
                  v-html="antonym.antonym"
                ></span
                ><span v-if="!isLast(index, article.antonyms)" class="article-content__text"
                  >,
                </span>
              </span>
            </div>
          </div>

          <template v-if="hasSublemmas">
            <div
              v-if="hasSynonyms || hasHyponyms || hasAntonyms"
              class="article-content__spacer"
            ></div>

            <div v-for="(sublemma, index) in article.sublemmas" :key="index">
              <b class="article-content__text article-content__text--primary">
                {{ sublemma.sublemma }}
              </b>
              <span v-if="sublemma.synonym" class="article-content__text pl-2">
                {{ sublemma.synonym }}
              </span>

              <template v-if="sublemma.reference.length > 0">
                <span class="article-content__text px-2">se</span>

                <ContentsWithLinks
                  v-for="(reference, referenceIndex) in sublemma.reference"
                  :key="referenceIndex"
                  :content="reference"
                  link-class="article-content__link"
                />
              </template>

              <span v-if="sublemma.comment" class="article-content__text pl-2"
                >(<span v-html="sublemma.comment"></span>)</span
              >
            </div>
          </template>

          <template v-if="article.word.asPer || article.word.endComment">
            <div v-if="hasSublemmas" class="article-content__spacer"></div>

            <div class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Jævnfør:
              </div>
              <div
                class="article-content__politiken-grid-content article-content__text article-content__text--primary"
              >
                <span v-if="article.word.asPer">{{ article.word.asPer }}</span
                ><span v-if="article.word.endComment">, </span>
                <span v-if="article.word.endComment">{{ article.word.endComment }}</span>
              </div>
            </div>
          </template>

          <template v-if="article.word.reference.length > 0">
            <div
              v-if="hasSublemmas && !article.word.asPer && !article.word.endComment"
              class="article-content__spacer"
            ></div>

            <div class="article-content__politiken-grid">
              <div class="article-content__text article-content__politiken-grid-label">
                Henvisning:
              </div>
              <div
                class="article-content__politiken-grid-content article-content__text article-content__text--primary"
              >
                <ContentsWithLinks
                  v-for="(reference, index) in article.word.reference"
                  :key="index"
                  :content="reference"
                  link-class="article-content__link"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
